import { useEffect, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import { Typography } from 'shared/ui/Typography'
import { Checkbox } from 'shared/ui/Checkbox'
import { Icon, isIconKey } from 'shared/ui/Icon'
import { Scrollbar } from 'shared/ui/Scrollbar'
import type { Integration } from 'entities/Integrations'
import { WidgetSearch, WidgetItem } from 'pages/chatbot/ui/UnifiedAgent/ui/Chat/ui/Widgets'
import type { IInstructionsFieldItem } from 'pages/chatbot/ui/UnifiedAgent/ui/InstructionsSection/store/instructionsWidgetStore'
import styles from 'pages/chatbot/ui/UnifiedAgent/ui/InstructionsSection/styles.module.scss'

type IInstructionsStep2Props = {
  items: IInstructionsFieldItem[]
  selectedKeys: Set<string>
  focusedIndex: number
  search: string
  onSearch: (value: string) => void
  onToggle: (key: string) => void
  onFocus: (index: number) => void
  onReconnect: (integration: Integration) => void
}

export const InstructionsStep2 = observer(
  ({
    items,
    selectedKeys,
    focusedIndex,
    search,
    onSearch,
    onToggle,
    onFocus,
    onReconnect,
  }: IInstructionsStep2Props) => {
    const focusedItemRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
      focusedItemRef.current?.scrollIntoView({ block: 'nearest' })
    }, [focusedIndex])

    return (
      <>
        <WidgetSearch value={search} onChange={onSearch} />
        {!items.length ? (
          <Typography
            variant={'body-rg-regular'}
            ariaLabel='noResults'
            className={styles.noResults}
          >
            No results found
          </Typography>
        ) : (
          <Scrollbar autoHeight autoHeightMax={290}>
            <div className={styles.fieldList}>
              {items.map((item, index) => {
                const isFocused = index === focusedIndex
                const isChecked = selectedKeys.has(item.key)
                const shouldReconnect = !item.isConnected && item.integration

                return (
                  <div key={item.key} ref={isFocused ? focusedItemRef : undefined}>
                    <WidgetItem
                      isFocused={isFocused}
                      leftNode={
                        shouldReconnect ? (
                          <Icon icon={'alertTriangleYellow'} fontSize={20} />
                        ) : (
                          <Checkbox
                            size={20}
                            checked={isChecked}
                            ariaLabel={item.name}
                            onChange={() => {}}
                          />
                        )
                      }
                      onClick={() => {
                        onFocus(index)
                        if (!shouldReconnect) onToggle(item.key)
                      }}
                      rightNode={
                        shouldReconnect ? (
                          <span
                            className={styles.reconnectBtn}
                            onClick={(e) => {
                              e.stopPropagation()
                              if (item.integration) onReconnect(item.integration)
                            }}
                          >
                            Reconnect
                          </span>
                        ) : undefined
                      }
                    >
                      {isIconKey(item.integrationKey) && (
                        <Icon icon={item.integrationKey} fontSize={20} />
                      )}
                      <Typography
                        variant={'body-rg-regular'}
                        ariaLabel={item.name}
                        ellipsis
                        tooltipProps={{ label: item.name }}
                      >
                        {item.name}
                      </Typography>
                    </WidgetItem>
                  </div>
                )
              })}
            </div>
          </Scrollbar>
        )}
      </>
    )
  }
)
